<template>
  <div class="rpp">
    <!-- Resumen -->
    <div class="rpp-summary">
      <div class="rpp-sum">
        <span class="rpp-sum__lbl">{{ $t('reservasPanels.payments.pendingLabel') }}</span>
        <span class="rpp-sum__val" :class="[remaining > 0 ? 'rpp-sum__val--due' : 'rpp-sum__val--ok', { 'rpp-bump': bump.remaining }]">{{ money(remaining) }}</span>
      </div>
      <div class="rpp-sum">
        <span class="rpp-sum__lbl">{{ $t('reservasPanels.payments.paidLabel') }}</span>
        <span class="rpp-sum__val rpp-sum__val--paid" :class="{ 'rpp-bump': bump.paid }">{{ money(paid) }}</span>
      </div>
      <div class="rpp-sum rpp-sum--right">
        <span class="rpp-sum__lbl">{{ $t('reservasPanels.payments.totalLabel') }}</span>
        <span class="rpp-sum__val" :class="{ 'rpp-bump': bump.total }">{{ money(total) }}</span>
      </div>
    </div>

    <!-- Líneas -->
    <div v-if="loading" class="rpp-loading">{{ $t('reservasPanels.payments.loading') }}</div>

    <transition-group v-else-if="payments.length" name="rpp-line" tag="div" class="rpp-lines">
      <div v-for="p in payments" :key="p.id" class="rpp-line" :class="{ 'rpp-line--paid': p.status === 'paid' }">
        <div class="rpp-line__ic">{{ methodIcon(p.method) }}</div>
        <div class="rpp-line__main">
          <div class="rpp-line__top">
            <span class="rpp-line__method">{{ methodLabel(p) }}</span>
            <span class="rpp-line__amount">{{ money(p.amount) }}</span>
          </div>
          <div class="rpp-line__bottom">
            <!-- Si se cobra en el datáfono, su estado manda: «en el datáfono», «sin respuesta»… -->
            <button
              v-if="p.datafono && datafonoAbierto(p.datafono)"
              type="button"
              class="rpp-chip rpp-chip--btn"
              :class="p.datafono.estado === 'sin_respuesta' ? 'rpp-chip--failed' : 'rpp-chip--pending'"
              @click="cobroEnCurso = p.datafono"
            >{{ $t('datafono.line.' + p.datafono.estado) }}</button>
            <span v-else class="rpp-chip" :class="'rpp-chip--' + p.status">{{ statusLabel(p.status) }}</span>
            <span v-if="p.note" class="rpp-line__note">· {{ p.note }}</span>
          </div>
        </div>
        <div class="rpp-line__actions">
          <template v-if="p.method === 'online' && p.status === 'pending'">
            <button class="rpp-ic-btn" :title="$t('reservasPanels.payments.sendWhatsappTitle')" :disabled="busyId === p.id" @click="askSend(p)"><i class="pi pi-send"></i></button>
            <button class="rpp-ic-btn" :title="$t('reservasPanels.payments.copyLinkTitle')" @click="copyLink(p)"><i class="pi pi-copy"></i></button>
            <button v-if="p.payment_url" class="rpp-ic-btn" :title="$t('datafono.qr.button')" @click="qrDe = p"><i class="pi pi-qrcode"></i></button>
          </template>
          <button v-if="p.is_manual" class="rpp-ic-btn" :title="$t('reservasPanels.payments.editAction')" @click="openEdit(p)"><i class="pi pi-pencil"></i></button>
          <button class="rpp-ic-btn rpp-ic-btn--del" :title="$t('reservasPanels.payments.deleteAction')" :disabled="busyId === p.id" @click="askDelete(p)"><i class="pi pi-trash"></i></button>
        </div>
      </div>
    </transition-group>

    <div v-else class="rpp-empty">{{ $t('reservasPanels.payments.empty') }}</div>

    <!-- Acciones -->
    <div class="rpp-actions">
      <button v-if="remaining > 0" class="rpp-add" type="button" @click="openAdd">
        <i class="pi pi-plus"></i> {{ $t('reservasPanels.payments.addPayment') }}
      </button>
      <span v-else class="rpp-fullypaid"><i class="pi pi-check-circle"></i> {{ $t('reservasPanels.payments.fullyPaid') }}</span>
      <button class="rpp-recalc" type="button" :title="$t('reservasPanels.payments.recalcTitle')" @click="recalculate"><i class="pi pi-refresh"></i></button>
    </div>
    <p v-if="error" class="rpp-error">{{ error }}</p>

    <!-- Modal Agregar / Editar -->
    <transition name="rpp-fade">
      <div v-if="modalOpen" class="rpp-modal" @click.self="closeModal">
        <div class="rpp-modal__card">
          <div class="rpp-modal__head">
            <h3>{{ editing ? $t('reservasPanels.payments.modalEditTitle') : $t('reservasPanels.payments.modalAddTitle') }}</h3>
            <button class="rpp-x" @click="closeModal">×</button>
          </div>

          <div class="rpp-modal__totals">
            <span>{{ $t('reservasPanels.payments.totalLabel') }} <b>{{ money(total) }}</b></span>
            <span>{{ $t('reservasPanels.payments.pendingLabel') }} <b :class="remainingForModal > 0 ? 'due' : 'ok'">{{ money(remainingForModal) }}</b></span>
          </div>

          <!-- Importe / % -->
          <label class="rpp-field__lbl">{{ $t('reservasPanels.payments.amountLabel') }}</label>
          <div class="rpp-amount">
            <div class="rpp-toggle">
              <button :class="{ on: mode === 'amount' }" @click="mode = 'amount'">{{ displayCurrency || '€' }}</button>
              <button :class="{ on: mode === 'percent' }" @click="mode = 'percent'">%</button>
            </div>
            <input v-model="amountInput" type="number" min="0" step="0.01" class="rpp-input" :placeholder="mode === 'percent' ? '50' : '0,00'" />
            <span class="rpp-amount__eq">= {{ money(computedAmount) }}</span>
          </div>
          <button class="rpp-rest" type="button" @click="fillRemaining">{{ $t('reservasPanels.payments.fillRemaining', { amount: money(remainingForModal) }) }}</button>

          <!-- Método (solo al agregar) -->
          <template v-if="!editing">
            <label class="rpp-field__lbl">{{ $t('reservasPanels.payments.methodLabel') }}</label>
            <div class="rpp-methods">
              <button v-for="m in methodOptions" :key="m.value" type="button" class="rpp-method" :class="{ on: method === m.value }" @click="method = m.value">
                <span class="rpp-method__ic">{{ m.icon }}</span>{{ m.label }}
              </button>
            </div>
            <label v-if="method === 'online'" class="rpp-check">
              <input type="checkbox" v-model="sendWa" /> {{ $t('reservasPanels.payments.sendWhatsappCheck') }}
            </label>

            <!-- Tarjeta con datáfono conectado: mandar el importe al aparato o solo anotarlo. -->
            <div v-if="method === 'card' && datafonos.length" class="rpp-df">
              <label class="rpp-df__op" :class="{ on: cardMode === 'datafono' }">
                <input v-model="cardMode" type="radio" value="datafono" />
                <span>
                  <b>{{ $t('datafono.add.sendTitle') }}</b>
                  <small>{{ $t('datafono.add.sendHint') }}</small>
                </span>
              </label>
              <select v-if="cardMode === 'datafono'" v-model="datafonoId" class="rpp-input rpp-df__sel">
                <option v-for="d in datafonos" :key="d.id" :value="d.id" :disabled="!d.en_linea">
                  {{ d.nombre }} — {{ d.en_linea ? $t('datafono.add.online') : $t('datafono.add.offline') }}
                </option>
              </select>
              <p v-if="cardMode === 'datafono' && !hayDatafonoEnLinea" class="rpp-df__aviso">{{ $t('datafono.add.noneOnline') }}</p>
              <label class="rpp-df__op" :class="{ on: cardMode === 'manual' }">
                <input v-model="cardMode" type="radio" value="manual" />
                <span>
                  <b>{{ $t('datafono.add.manualTitle') }}</b>
                  <small>{{ $t('datafono.add.manualHint') }}</small>
                </span>
              </label>
            </div>
          </template>
          <template v-else>
            <label class="rpp-field__lbl">{{ $t('reservasPanels.payments.methodLabel') }}</label>
            <div class="rpp-methods">
              <button v-for="m in methodOptions.filter(o => o.value !== 'online')" :key="m.value" type="button" class="rpp-method" :class="{ on: method === m.value }" @click="method = m.value">
                <span class="rpp-method__ic">{{ m.icon }}</span>{{ m.label }}
              </button>
            </div>
          </template>

          <label class="rpp-field__lbl">{{ $t('reservasPanels.payments.noteLabel') }}</label>
          <input v-model="note" type="text" class="rpp-input" maxlength="1000" :placeholder="$t('reservasPanels.payments.notePlaceholder')" />

          <div class="rpp-modal__foot">
            <span v-if="modalError" class="rpp-error">{{ modalError }}</span>
            <button class="rpp-btn rpp-btn--ghost" @click="closeModal">{{ $t('reservasPanels.payments.cancel') }}</button>
            <button class="rpp-btn rpp-btn--primary" :disabled="saving || computedAmount <= 0 || (cobrarEnDatafono && !datafonoElegidoEnLinea)" @click="submit">
              <span v-if="saving" class="rpp-spin"></span>
              <span v-else>{{ editing ? $t('reservasPanels.payments.save') : (method === 'online' ? $t('reservasPanels.payments.generateLink') : (cobrarEnDatafono ? $t('datafono.add.submit') : $t('reservasPanels.payments.registerPayment'))) }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal confirmar eliminación -->
    <transition name="rpp-fade">
      <div v-if="confirmDelete" class="rpp-modal" @click.self="confirmDelete = null">
        <div class="rpp-modal__card rpp-modal__card--sm">
          <div class="rpp-confirm__ic"><i class="pi pi-trash"></i></div>
          <h3 class="rpp-confirm__title">{{ $t('reservasPanels.payments.deleteConfirmTitle') }}</h3>
          <p class="rpp-confirm__text">
            {{ $t('reservasPanels.payments.deleteText1') }} <b>{{ money(confirmDelete.amount) }}</b> · {{ methodLabel(confirmDelete) }}.
            <template v-if="confirmDelete.method === 'online' && confirmDelete.status === 'pending'"> {{ $t('reservasPanels.payments.deleteLinkVoided') }}</template>
            {{ $t('reservasPanels.payments.deleteIrreversible') }}
          </p>
          <div class="rpp-confirm__actions">
            <button class="rpp-btn rpp-btn--ghost" :disabled="busyId === confirmDelete.id" @click="confirmDelete = null">{{ $t('reservasPanels.payments.cancel') }}</button>
            <button class="rpp-btn rpp-btn--danger" :disabled="busyId === confirmDelete.id" @click="doRemove">
              <span v-if="busyId === confirmDelete.id" class="rpp-spin"></span>
              <span v-else>{{ $t('reservasPanels.payments.deleteBtn') }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- El cobro en el datáfono, en directo -->
    <CobroEnDatafonoModal
      v-if="cobroEnCurso"
      :key="cobroEnCurso.uuid"
      :cobro-inicial="cobroEnCurso"
      @finished="load(true)"
      @close="cobroEnCurso = null; load(true)"
      @retry="reintentarDatafono"
    />

    <!-- El enlace de pago como QR, para cobrar en recepción sin datáfono -->
    <QrDePagoModal v-if="qrDe" :url="qrDe.payment_url" :importe="money(qrDe.amount)" @close="qrDe = null" />

    <!-- Modal confirmar envío del link de pago -->
    <transition name="rpp-fade">
      <div v-if="confirmSend" class="rpp-modal" @click.self="confirmSend = null">
        <div class="rpp-modal__card rpp-modal__card--sm">
          <div class="rpp-confirm__ic rpp-confirm__ic--send"><i class="pi pi-send"></i></div>
          <h3 class="rpp-confirm__title">{{ $t('reservasPanels.payments.sendConfirmTitle') }}</h3>
          <p class="rpp-confirm__text">
            {{ $t('reservasPanels.payments.sendText1') }}
            <b>{{ money(confirmSend.amount) }}{{ confirmSend.currency ? ' ' + confirmSend.currency : '' }}</b>.
          </p>
          <div class="rpp-confirm__actions">
            <button class="rpp-btn rpp-btn--ghost" :disabled="busyId === confirmSend.id" @click="confirmSend = null">{{ $t('reservasPanels.payments.cancel') }}</button>
            <button class="rpp-btn rpp-btn--primary" :disabled="busyId === confirmSend.id" @click="doSend">
              <span v-if="busyId === confirmSend.id" class="rpp-spin"></span>
              <span v-else>{{ $t('reservasPanels.payments.sendBtn') }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { resolveReservationCurrency, formatMoneyCode } from '@/utils/formatters';
import CobroEnDatafonoModal from '@/components/Reservas/CobroEnDatafonoModal.vue';
import QrDePagoModal from '@/components/Reservas/QrDePagoModal.vue';

export default {
  name: 'ReservationPaymentsPanel',

  components: { CobroEnDatafonoModal, QrDePagoModal },

  props: {
    reservationData: { type: Object, required: true },
  },

  data() {
    return {
      loading: false, saving: false, busyId: null,
      error: null, modalError: null,
      total: 0, paid: 0, remaining: 0, currency: null,
      payments: [],
      modalOpen: false, editing: null,
      // Las pasarelas que este anfitrión tiene conectadas de verdad. Antes la opción
      // «online» salía SIEMPRE y decía «Wompi» o «Stripe» según el país, estuviera
      // conectada o no: se ofrecía generar un enlace con una pasarela que no existe.
      // Vacío mientras se consulta; la opción no se pinta hasta saberlo.
      pasarelas: [],
      // La pasarela que de verdad cobrará un enlace, según el backend (undefined = no lo ha dicho).
      gateway: undefined,
      // Nombre de la cuenta concreta que cobrará («Redsys pruebas»): lo manda el servidor.
      gatewayAccount: null,
      mode: 'amount', amountInput: '', method: 'online', note: '', sendWa: true,
      confirmDelete: null,
      confirmSend: null,
      // Datáfonos del anfitrión (los manda el servidor con los pagos).
      datafonos: [],
      cardMode: 'manual',
      datafonoId: null,
      cobroEnCurso: null,
      qrDe: null,
      bump: { total: false, paid: false, remaining: false },
    };
  },

  computed: {
    reservationId() { return this.reservationData?.id; },
    // Moneda del alojamiento: la MISMA fuente que usa el desglose y los cargos
    // (la columna `currency` de la propiedad), para que todo el panel cuadre.
    displayCurrency() {
      // Moneda REAL por país (la columna `currency` de la propiedad no es fiable:
      // a veces trae EUR en propiedades de Colombia → mostraría el importe en EUR).
      return resolveReservationCurrency(this.reservationData) || this.currency || 'EUR';
    },
    // Colombia: Stripe→Wompi y Bizum→Nequi (pasarelas locales).
    isColombia() {
      return String(this.reservationData?.propiedad?.code_pais || '').toUpperCase() === 'CO';
    },
    // Firma del total (precio + cargos): cambia cuando se añade/edita/elimina un
    // cargo → recargamos para recalcular el saldo pendiente.
    chargesKey() {
      const r = this.reservationData || {};
      const charges = Array.isArray(r.invoice_charges) ? r.invoice_charges : [];
      const sum = charges.reduce((s, c) => s + (Number(c.total_with_vat ?? c.total ?? 0) || 0), 0);
      // Incluye la ampliación pendiente: cambia extension_price/status (no `price`),
      // así que sin esto el panel no se enteraría de una ampliación recién creada.
      return [r.id, r.price, charges.length, sum, r.extension_status, r.extension_price].join('|');
    },
    /** El nombre de la pasarela que de verdad va a cobrar. */
    pasarelaLabel() {
      const nombres = { bold: 'Bold', wompi: 'Wompi', stripe: 'Stripe', redsys: 'Redsys' };
      /*
       * La misma regla que el backend (PaymentLinkService::preferredProviderForUser):
       *  - Bold solo si está conectada Y la moneda es una que Bold cobra (COP, PEN,
       *    USD). Antes bastaba con tenerla conectada: un alojamiento de España en
       *    EUR con Stripe enseñaba «Bold», aunque el enlace salía de Stripe.
       *  - Colombia (país CO o pesos) → Wompi.
       *  - El resto → Stripe.
       */
      // Lo que diga el backend manda: es quien crea el enlace.
      if (this.gateway !== undefined) {
        if (!this.gateway) return null;
        const pasarela = nombres[this.gateway] || null;
        // Con el nombre de la cuenta, si no es el mismo que la pasarela: así se ve en la
        // ficha si va a cobrar la cuenta que el anfitrión espera.
        const cuenta = (this.gatewayAccount || '').trim();
        return pasarela && cuenta && cuenta.toLowerCase() !== pasarela.toLowerCase()
          ? `${pasarela} · ${cuenta}`
          : pasarela;
      }

      const moneda = String(this.displayCurrency || '').toUpperCase();
      const conectada = (p) => this.pasarelas.includes(p);
      let elegida = null;
      const pais = String(this.reservationData?.propiedad?.code_pais || '').toUpperCase();
      // Bold: pesos o soles, o dólares en Colombia/Perú. USD fuera de ahí → Stripe.
      const zonaBold = ['COP', 'PEN'].includes(moneda) || (moneda === 'USD' && ['CO', 'PE'].includes(pais));
      if (conectada('bold') && zonaBold) elegida = 'bold';
      else if (this.isColombia || moneda === 'COP') elegida = conectada('wompi') ? 'wompi' : null;
      // Fuera de Colombia, Stripe; Redsys solo si NO hay Stripe y se cobra en euros.
      else if (conectada('stripe')) elegida = 'stripe';
      else if (conectada('redsys') && (moneda === 'EUR' || !moneda)) elegida = 'redsys';

      return elegida ? nombres[elegida] : null;
    },

    methodOptions() {
      const co = this.isColombia;
      return [
        // Solo si hay pasarela conectada: sin ella no hay enlace que generar. Los
        // demás métodos se quedan siempre, que esos no generan nada — solo anotan
        // un pago que ya se hizo.
        ...(this.pasarelaLabel
          ? [{ value: 'online', label: this.pasarelaLabel, icon: '💳' }]
          : []),
        { value: 'cash',     label: this.$t('reservasPanels.payments.methodCash'),      icon: '💵' },
        { value: 'card',     label: this.$t('reservasPanels.payments.methodCard'), icon: '📟' },
        { value: 'transfer', label: this.$t('reservasPanels.payments.methodTransfer'), icon: '🏦' },
        { value: 'bizum',    label: co ? 'Nequi' : 'Bizum', icon: '📲' },
        { value: 'ota',      label: this.$t('reservasPanels.payments.methodOta'), icon: '🌐' },
      ];
    },
    // Restante de referencia en el modal (al editar excluye la propia línea).
    hayDatafonoEnLinea() {
      return this.datafonos.some((d) => d.en_linea);
    },
    cobrarEnDatafono() {
      return !this.editing && this.method === 'card' && this.datafonos.length > 0 && this.cardMode === 'datafono';
    },
    datafonoElegidoEnLinea() {
      const d = this.datafonos.find((x) => x.id === this.datafonoId);
      return !!(d && d.en_linea);
    },
    remainingForModal() {
      if (!this.editing) return this.remaining;
      return Math.max(0, this.round(this.remaining + Number(this.editing.amount || 0)));
    },
    computedAmount() {
      const v = parseFloat(this.amountInput) || 0;
      if (this.mode === 'percent') return this.round(this.total * (v / 100));
      return this.round(v);
    },
  },

  created() {
    this.load();
    this.cargarPasarelas();
    // Realtime: si llega un evento de servicio (creación de solicitud o pago
    // confirmado) de ESTA reserva, recargamos líneas/saldo SIN parpadeo (silencioso).
    this._onRtRefresh = (p) => {
      try {
        const rid = p && (p.reservation_id || p.reserva_id);
        if (!rid || Number(rid) === Number(this.reservationId)) {
          this.load(true);
        }
      } catch (e) { /* noop */ }
    };
    this.$root.$on('service-payment-confirmed', this._onRtRefresh);
  },

  beforeDestroy() {
    if (this._onRtRefresh) this.$root.$off('service-payment-confirmed', this._onRtRefresh);
  },

  watch: {
    // Recalcula el saldo cuando cambian los cargos/ampliación de la reserva.
    chargesKey() { this.load(true); },
  },

  methods: {
    round(n) { return Math.round((Number(n) || 0) * 100) / 100; },
    money(v) {
      // Formato por moneda: COP/ARS/CLP/PEN sin decimales (peso), EUR/USD con 2.
      return formatMoneyCode(v, this.displayCurrency);
    },
    /**
     * Qué pasarelas tiene conectadas este anfitrión.
     *
     * Se consulta una vez al abrir la ficha. Si la llamada falla nos quedamos sin
     * ninguna y la opción de generar enlace no se pinta: es preferible que el
     * operador registre el pago a mano a ofrecerle un botón que va a fallar.
     */
    async cargarPasarelas() {
      try {
        const { data } = await axios.get('/me/payment-accounts');
        const cuentas = data?.data || [];
        this.pasarelas = [...new Set(
          cuentas.filter((c) => c.status === 'lista' || c.is_verified).map((c) => c.provider),
        )];
      } catch (e) {
        this.pasarelas = [];
      }

      // Si «online» estaba elegido y resulta que no hay pasarela, se pasa al
      // primero que sí sirve: dejarlo seleccionado llevaría a un botón muerto.
      if (this.method === 'online' && !this.pasarelaLabel) {
        this.method = 'cash';
      }
    },

    methodIcon(m) { return (this.methodOptions.find(o => o.value === m) || {}).icon || '💳'; },
    methodLabel(p) {
      if (p.method === 'online') {
        // Lo que diga el pago; si es viejo y no lo guardó, lo de siempre.
        const nombres = { bold: 'Bold', wompi: 'Wompi', stripe: 'Stripe', redsys: 'Redsys' };
        return nombres[p.provider] || (this.isColombia ? 'Wompi' : 'Stripe');
      }
      return (this.methodOptions.find(o => o.value === p.method) || {}).label || p.method;
    },
    statusLabel(s) { return { paid: this.$t('reservasPanels.payments.statusPaid'), pending: this.$t('reservasPanels.payments.statusPending'), failed: this.$t('reservasPanels.payments.statusFailed'), cancelled: this.$t('reservasPanels.payments.statusCancelled') }[s] || s; },

    apply(data) {
      const prev = { total: this.total, paid: this.paid, remaining: this.remaining };
      this.total = data.total ?? 0;
      this.paid = data.paid ?? 0;
      this.remaining = data.remaining ?? 0;
      this.currency = data.currency ?? null;
      this.payments = Array.isArray(data.payments) ? data.payments : [];
      if (Object.prototype.hasOwnProperty.call(data, 'gateway')) this.gateway = data.gateway || null;
      this.gatewayAccount = data.gateway_account || null;
      this.datafonos = Array.isArray(data.datafonos) ? data.datafonos : [];
      // refleja el estado derivado en la reserva (badge "Pago pendiente/Parcial/Pagado")
      this.$emit('changed', { pay: data.pay, total: this.total, paid: this.paid, remaining: this.remaining });
      // "Pop" en los importes que cambian (no en la primera carga, para no animar al abrir).
      if (this._loaded) this.bumpChanged(prev);
      this._loaded = true;
    },

    // Anima (pop) los importes Pendiente/Cobrado/Total que cambiaron de valor.
    bumpChanged(prev) {
      ['total', 'paid', 'remaining'].forEach((k) => {
        if (Number(prev[k]) !== Number(this[k])) {
          this.$set(this.bump, k, false);
          this.$nextTick(() => {
            this.$set(this.bump, k, true);
            setTimeout(() => this.$set(this.bump, k, false), 650);
          });
        }
      });
    },

    async load(silent = false) {
      if (!this.reservationId) return;
      if (!silent) this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(`/reservas/${this.reservationId}/payments`);
        this.apply(data);
      } catch (e) {
        if (!silent) this.error = e?.response?.data?.message || this.$t('reservasPanels.payments.loadError');
      } finally { if (!silent) this.loading = false; }
    },

    openAdd() {
      this.editing = null; this.modalError = null;
      this.mode = 'amount';
      this.amountInput = this.remaining > 0 ? String(this.remaining) : '';
      this.method = 'online'; this.note = ''; this.sendWa = true;
      // Si hay un datáfono en línea, «Tarjeta» cobra en él por defecto; si no, se anota.
      const enLinea = this.datafonos.find((d) => d.en_linea);
      this.cardMode = enLinea ? 'datafono' : 'manual';
      this.datafonoId = enLinea ? enLinea.id : (this.datafonos[0] && this.datafonos[0].id) || null;
      this.modalOpen = true;
    },
    datafonoAbierto(c) {
      return ['pendiente', 'enviado', 'sin_respuesta'].indexOf(c.estado) !== -1;
    },
    // «Volver a intentar» desde el seguimiento: el mismo importe, otra vez al datáfono.
    reintentarDatafono(importe) {
      this.cobroEnCurso = null;
      this.openAdd();
      this.method = 'card';
      this.amountInput = String(importe);
    },
    openEdit(p) {
      this.editing = p; this.modalError = null;
      this.mode = 'amount'; this.amountInput = String(p.amount); this.method = p.method; this.note = p.note || '';
      this.modalOpen = true;
    },
    closeModal() { this.modalOpen = false; this.editing = null; },
    fillRemaining() { this.mode = 'amount'; this.amountInput = String(this.remainingForModal); },

    async submit() {
      if (this.computedAmount <= 0) { this.modalError = this.$t('reservasPanels.payments.amountRequired'); return; }
      this.saving = true; this.modalError = null;
      try {
        if (this.editing) {
          const { data } = await axios.put(`/reservas/${this.reservationId}/payments/${this.editing.id}`, {
            amount: this.computedAmount, method: this.method, note: this.note || null,
          });
          this.apply(data);
        } else {
          const payload = { method: this.method, amount: this.computedAmount, note: this.note || null };
          if (this.method === 'online') payload.send_whatsapp = this.sendWa;
          if (this.cobrarEnDatafono) payload.terminal_id = this.datafonoId;
          const { data } = await axios.post(`/reservas/${this.reservationId}/payments`, payload);
          this.apply(data);
          // Va al datáfono: se abre el seguimiento en directo.
          if (data && data.cobro_datafono) this.cobroEnCurso = data.cobro_datafono;
        }
        this.closeModal();
      } catch (e) {
        this.modalError = e?.response?.data?.message || this.$t('reservasPanels.payments.saveError');
      } finally { this.saving = false; }
    },

    askDelete(p) {
      this.error = null;
      this.confirmDelete = p;
    },

    async doRemove() {
      const p = this.confirmDelete;
      if (!p) return;
      this.busyId = p.id; this.error = null;
      try {
        const { data } = await axios.delete(`/reservas/${this.reservationId}/payments/${p.id}`);
        this.apply(data);
        this.confirmDelete = null;
      } catch (e) {
        this.error = e?.response?.data?.message || this.$t('reservasPanels.payments.deleteError');
      } finally { this.busyId = null; }
    },

    askSend(p) {
      this.error = null;
      this.confirmSend = p;
    },

    async doSend() {
      const p = this.confirmSend;
      if (!p) return;
      await this.sendWhatsapp(p);
      if (!this.error) this.confirmSend = null;
    },

    async sendWhatsapp(p) {
      this.busyId = p.id; this.error = null;
      try {
        await axios.post(`/reservas/${this.reservationId}/payments/${p.id}/send-whatsapp`);
      } catch (e) {
        this.error = e?.response?.data?.message || this.$t('reservasPanels.payments.sendLinkError');
      } finally { this.busyId = null; }
    },

    copyLink(p) {
      if (!p.payment_url) return;
      navigator.clipboard?.writeText(p.payment_url);
    },

    async recalculate() {
      this.error = null;
      try {
        const { data } = await axios.post(`/reservas/${this.reservationId}/payments/recalculate`);
        this.apply(data);
      } catch (e) {
        this.error = e?.response?.data?.message || this.$t('reservasPanels.payments.recalcError');
      }
    },
  },
};
</script>

<style scoped>
.rpp { font-family: inherit; color: #1a1f2e; }
.rpp-summary { display: flex; gap: 10px; margin-bottom: 14px; }
.rpp-sum { flex: 1; border: 1px solid #eceef3; border-radius: 12px; padding: 10px 12px; background: #fff; }
.rpp-sum__lbl { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #8a8f9c; font-weight: 600; }
.rpp-sum__val { display: inline-block; font-size: 17px; font-weight: 700; margin-top: 3px; transition: color .3s ease; }
.rpp-sum__val--paid { color: #16a34a; }
.rpp-sum__val--due { color: #e65100; }
.rpp-sum__val--ok { color: #16a34a; }
.rpp-sum--right { text-align: right; }

.rpp-loading, .rpp-empty { color: #9ca3af; font-size: 13px; padding: 10px 2px; }
.rpp-lines { display: flex; flex-direction: column; gap: 8px; }
.rpp-line { display: flex; gap: 11px; align-items: center; border: 1px solid #eceef3; border-radius: 12px; padding: 10px 12px; background: #fff; }
.rpp-line__ic { font-size: 18px; width: 30px; text-align: center; }
.rpp-line__main { flex: 1; min-width: 0; }
.rpp-line__top { display: flex; justify-content: space-between; gap: 8px; align-items: baseline; }
.rpp-line__method { font-weight: 600; font-size: 14px; }
.rpp-line__amount { font-weight: 700; font-size: 14px; }
.rpp-line__bottom { display: flex; align-items: center; gap: 6px; margin-top: 2px; }
.rpp-line__note { font-size: 12px; color: #9ca3af; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rpp-chip { font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px; }
.rpp-chip--paid { background: #e6f7ee; color: #16a34a; }
.rpp-chip--pending { background: #fff3e0; color: #e65100; }
.rpp-chip--failed, .rpp-chip--cancelled { background: #fee2e2; color: #dc2626; }
.rpp-line__actions { display: flex; gap: 4px; }
.rpp-ic-btn { width: 30px; height: 30px; border-radius: 8px; border: 1px solid #e7e9f0; background: #fff; color: #5b6470; cursor: pointer; display: grid; place-items: center; }
.rpp-ic-btn:hover { background: #f5f6f8; }
.rpp-ic-btn--del { color: #dc2626; border-color: #f3d4d4; }
.rpp-ic-btn:disabled { opacity: .5; cursor: default; }

.rpp-actions { display: flex; gap: 8px; margin-top: 14px; }
.rpp-add { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: 1.5px dashed #c7bdf2; background: #f6f3ff; color: #6c4cf0; border-radius: 11px; padding: 11px; font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
.rpp-add:hover { background: #efe9ff; }
.rpp-add:disabled { opacity: .5; cursor: default; }
.rpp-fullypaid { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 7px; color: #16a34a; background: #e6f7ee; border: 1px solid #c7ecd5; border-radius: 11px; padding: 11px; font-size: 13.5px; font-weight: 600; }
.rpp-recalc { width: 44px; border: 1px solid #e7e9f0; background: #fff; color: #5b6470; border-radius: 11px; cursor: pointer; }
.rpp-recalc:hover { background: #f5f6f8; }
.rpp-error { color: #dc2626; font-size: 12.5px; font-weight: 600; margin: 8px 0 0; }

/* modal */
/* Si la tarjeta es más alta que la pantalla, se desplaza la capa entera (no la tarjeta por
   dentro): margin:auto la centra cuando cabe y la deja empezar arriba cuando no, así los
   botones del pie siempre se alcanzan. Con place-items:center se salía por arriba y por abajo. */
.rpp-modal { position: fixed; inset: 0; background: rgba(15,17,23,.5); display: flex; z-index: 9999; padding: 16px; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
.rpp-modal__card { margin: auto; background: #fff; border-radius: 18px; width: 100%; max-width: 420px; padding: 22px; box-shadow: 0 30px 70px -30px rgba(0,0,0,.5); }
.rpp-modal__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.rpp-modal__head h3 { margin: 0; font-size: 18px; font-weight: 700; }
.rpp-x { border: 0; background: none; font-size: 24px; line-height: 1; color: #9ca3af; cursor: pointer; }
.rpp-modal__totals { display: flex; justify-content: space-between; background: #f7f8fb; border-radius: 10px; padding: 10px 12px; font-size: 13px; color: #5b6470; margin-bottom: 14px; }
.rpp-modal__totals b { color: #1a1f2e; }
.rpp-modal__totals .due { color: #e65100; }
.rpp-modal__totals .ok { color: #16a34a; }
.rpp-field__lbl { display: block; font-size: 12px; font-weight: 600; color: #6b7280; margin: 12px 0 5px; }
.rpp-amount { display: flex; align-items: center; gap: 8px; }
.rpp-toggle { display: flex; border: 1px solid #e3e6ee; border-radius: 9px; overflow: hidden; }
.rpp-toggle button { border: 0; background: #fff; padding: 8px 12px; font-weight: 700; color: #8a8f9c; cursor: pointer; }
.rpp-toggle button.on { background: #6c4cf0; color: #fff; }
.rpp-input { flex: 1; box-sizing: border-box; border: 1px solid #e3e6ee; border-radius: 10px; padding: 10px 12px; font-size: 14px; font-family: inherit; width: 100%; }
.rpp-input:focus { outline: none; border-color: #b9a8f7; box-shadow: 0 0 0 3px #f1ecff; }
.rpp-amount__eq { font-size: 12.5px; color: #8a8f9c; white-space: nowrap; }
.rpp-rest { margin-top: 7px; border: 0; background: none; color: #6c4cf0; font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 0; }
.rpp-methods { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.rpp-method { display: inline-flex; align-items: center; gap: 7px; border: 1px solid #e3e6ee; background: #fff; border-radius: 10px; padding: 10px 12px; font-family: inherit; font-size: 13.5px; font-weight: 600; color: #4b5563; cursor: pointer; }
.rpp-method.on { border-color: #6c4cf0; background: #f6f3ff; color: #6c4cf0; }
.rpp-method__ic { font-size: 15px; }
.rpp-check { display: flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 13px; color: #4b5563; }
/* Tarjeta con datáfono: mandar al aparato o solo anotar. */
.rpp-chip--btn { border: 0; cursor: pointer; font-family: inherit; }
.rpp-chip--btn:hover { filter: brightness(0.96); }
.rpp-df { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
.rpp-df__op { display: flex; gap: 10px; align-items: flex-start; padding: 10px 12px; border: 1.5px solid #e5e7eb; border-radius: 10px; cursor: pointer; }
.rpp-df__op.on { border-color: #1e293b; }
.rpp-df__op input { margin-top: 3px; }
.rpp-df__op b { display: block; font-size: 13px; color: #111827; }
.rpp-df__op small { display: block; font-size: 12px; line-height: 1.45; color: #6b7280; }
.rpp-df__sel { margin: -2px 0 2px 30px; width: calc(100% - 30px); }
.rpp-df__aviso { margin: -2px 0 2px 30px; font-size: 12px; color: #b45309; }
.rpp-modal__foot { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.rpp-modal__foot .rpp-error { margin: 0 auto 0 0; }
.rpp-btn { border-radius: 10px; padding: 10px 18px; font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer; border: 1px solid transparent; display: inline-flex; align-items: center; gap: 7px; }
.rpp-btn--ghost { background: #fff; border-color: #e5e7eb; color: #6b7280; }
.rpp-btn--primary { background: #6c4cf0; color: #fff; min-width: 130px; justify-content: center; }
.rpp-btn--primary:disabled { opacity: .55; cursor: default; }
.rpp-btn--danger { background: #dc2626; color: #fff; min-width: 130px; justify-content: center; }
.rpp-btn--danger:hover:not(:disabled) { background: #c41f1f; }
.rpp-btn--danger:disabled { opacity: .55; cursor: default; }

/* confirmación de borrado */
.rpp-modal__card--sm { max-width: 380px; text-align: center; }
.rpp-confirm__ic { width: 54px; height: 54px; margin: 4px auto 14px; border-radius: 50%; background: #fee2e2; color: #dc2626; display: grid; place-items: center; font-size: 22px; }
.rpp-confirm__ic--send { background: #ede9fe; color: #6c4cf0; }
.rpp-confirm__title { margin: 0 0 8px; font-size: 18px; font-weight: 700; }
.rpp-confirm__text { margin: 0 0 22px; font-size: 14px; line-height: 1.5; color: #56544d; }
.rpp-confirm__text b { color: #1a1f2e; }
.rpp-confirm__actions { display: flex; gap: 10px; justify-content: center; }
.rpp-spin { width: 15px; height: 15px; border-radius: 50%; border: 2px solid rgba(255,255,255,.5); border-top-color: #fff; display: inline-block; animation: rpp-spin .7s linear infinite; }
@keyframes rpp-spin { to { transform: rotate(360deg); } }
.rpp-fade-enter-active, .rpp-fade-leave-active { transition: opacity .2s; }
.rpp-fade-enter, .rpp-fade-leave-to { opacity: 0; }

/* Actualización en vivo: "pop" de los importes que cambian (Pendiente/Cobrado/Total) */
@keyframes rpp-pop { 0% { transform: scale(1); } 35% { transform: scale(1.15); } 100% { transform: scale(1); } }
.rpp-bump { animation: rpp-pop .6s cubic-bezier(.22,.61,.36,1); transform-origin: left center; }
.rpp-sum--right .rpp-bump { transform-origin: right center; }

/* Altas / bajas / reordenado de líneas de cobro */
.rpp-line { transition: background .35s ease, border-color .35s ease, box-shadow .35s ease; }
.rpp-line--paid { background: #f7fcf9; border-color: #cdeed7; }
.rpp-line-enter-active { transition: all .45s cubic-bezier(.22,.61,.36,1); }
.rpp-line-leave-active { transition: all .35s ease; }
.rpp-line-enter, .rpp-line-leave-to { opacity: 0; transform: translateY(-10px); }
.rpp-line-move { transition: transform .45s cubic-bezier(.22,.61,.36,1); }
</style>
